import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { courtsApi, courtTypesApi, extractList } from '../../api/facades';

const inputClass = 'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const CourtCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [court_type_id, setCourtTypeId] = useState<number | ''>('');
  const [court_name, setCourtName] = useState('');
  const [is_active, setIsActive] = useState<'Yes' | 'No'>('Yes');

  const { data: typesRes } = useQuery({
    queryKey: ['court-types'],
    queryFn: () => courtTypesApi.list({}),
  });
  const types = extractList(typesRes);

  const create = useMutation({
    mutationFn: () => courtsApi.create({ court_type_id: court_type_id as number, court_name, is_active }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['courts'] });
      const id = data?.data?.id ?? (data?.data && (data.data as any).id);
      if (id) navigate(`/dashboard/settings/courts/${id}`);
      else navigate('/dashboard/settings/courts');
      toast.success('Court created.');
    },
    onError: (e: any) => toast.error(e?.message || 'Create failed.'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!court_name.trim() || typeof court_type_id !== 'number') return;
    create.mutate();
  };

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <h1 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-4">New Court</h1>
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Court" hint="Required">
          <select className={inputClass} value={court_type_id === '' ? '' : court_type_id} onChange={(e) => setCourtTypeId(e.target.value === '' ? '' : Number(e.target.value))} required>
            <option value="">Select court</option>
            {types.map((t: any) => <option key={t.id} value={t.id}>{t.court_type_name}</option>)}
          </select>
        </FormField>
        <FormField label="Court name">
          <input className={inputClass} value={court_name} onChange={(e) => setCourtName(e.target.value)} placeholder="e.g. District Court 1" required />
        </FormField>
        <FormField label="Active">
          <select className={inputClass} value={is_active} onChange={(e) => setIsActive(e.target.value as 'Yes' | 'No')}>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </FormField>
        <div className="md:col-span-2 flex gap-2 mt-2">
          <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!court_name.trim() || typeof court_type_id !== 'number' || create.isPending}>{create.isPending ? 'Creating…' : 'Create'}</Button>
        </div>
      </form>
    </div>
  );
};

export default CourtCreatePage;
